import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import {
  getTicketsForKiosk,
  getHistoryEvents,
  createTicket,
  addHistoryEvent,
  getOpenTicketForKiosk,
  escalateTicketToZendesk,
  CLOSED_STATUS,
  type Ticket,
  type TicketHistory,
  type TicketIssueType,
  type TicketIssueCategory,
  type TicketStatus,
} from "../../api/ticket.ts";
import { statusBadge } from "../tech/ticketStatus.tsx";
import { TicketHistoryTimeline, SolutionCallout } from "../tech/ticketHistoryTimeline.tsx";
import { SearchableSelect } from "../../components/ui/searchableSelect.tsx";
import { buildIssueTypeOptions } from "../../util/issueTypeOptions.ts";
import styles from "../../styles/formPage.module.scss";

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString();
}

/* ── Tab ──────────────────────────────────────────────────── */

export function KioskProblemsTab({
  kioskId,
  canWrite,
  onReady,
}: {
  kioskId: number;
  canWrite: boolean;
  onReady?: () => void;
}) {
  const { cache, session } = useAuth();
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const issueTypes = useMemo(() => (cache?.TicketIssueTypes ?? []) as TicketIssueType[], [cache]);
  const issueCategories = useMemo(() => (cache?.IssueCategories ?? []) as TicketIssueCategory[], [cache]);
  const statuses = useMemo(() => (cache?.TicketStatuses ?? []) as TicketStatus[], [cache]);
  const issueLabel = useCallback(
    (issueId: number) => issueTypes.find((i) => i.ID === issueId)?.Issue ?? "Auto",
    [issueTypes],
  );
  const filteredStatuses = useMemo(() => statuses.filter((s) => s.Title !== "AutoGenerated"), [statuses]);

  // Load-once guard — StrictMode double-mount would fire getTicketsForKiosk twice.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    if (kioskId < 1) {
      setTickets([]);
      onReady?.();
      return;
    }
    getTicketsForKiosk(kioskId)
      .then((r) => {
        if (!r.bError && r.oData) setTickets(r.oData);
        else {
          setTickets([]);
          setError(r.sMessage ?? "Could not load problems.");
        }
      })
      .catch((e: unknown) => {
        setTickets([]);
        setError(String(e));
      })
      .finally(() => onReady?.());
  }, [kioskId, onReady]);

  const reload = useCallback(async () => {
    if (kioskId < 1) return;
    const r = await getTicketsForKiosk(kioskId);
    if (!r.bError && r.oData) setTickets(r.oData);
  }, [kioskId]);

  const visible = useMemo(
    () => (tickets ?? []).filter((t) => showClosed || t.Status !== CLOSED_STATUS),
    [tickets, showClosed],
  );
  const closedCount = useMemo(
    () => (tickets ?? []).filter((t) => t.Status === CLOSED_STATUS).length,
    [tickets],
  );

  if (tickets === null) {
    return <div className={styles.hint} style={{ padding: "var(--space-4)" }}>Loading problems…</div>;
  }

  return (
    <div className={styles.formContainerWide}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
        <h3 className={styles.sectionTitle} style={{ margin: 0, flex: 1 }}>
          Problems &amp; history
        </h3>
        {closedCount > 0 && (
          <label className={styles.checkLabel} style={{ marginBottom: 0 }}>
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            Show closed ({closedCount})
          </label>
        )}
        {canWrite && (
          <button className={styles.gridCreateBtn} onClick={() => setShowCreate((v) => !v)}>
            + New problem
          </button>
        )}
      </div>

      {error && (
        <div className={`${styles.statusMsg} ${styles.statusError}`} style={{ marginBottom: "var(--space-3)" }}>
          {error}
        </div>
      )}

      {showCreate && canWrite && (
        <NewProblemForm
          kioskId={kioskId}
          issueTypes={issueTypes}
          issueCategories={issueCategories}
          statuses={filteredStatuses}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {visible.length === 0 ? (
        <p className={styles.hint}>
          {closedCount > 0 && !showClosed ? "No open problems. Tick “Show closed” to see resolved ones." : "No problems recorded for this kiosk."}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {visible.map((t) => (
            <ProblemRow
              key={t.ID}
              ticket={t}
              issueLabel={issueLabel(t.IssueID)}
              statuses={filteredStatuses}
              canWrite={canWrite}
              authorId={session?.UID ?? -1}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── One problem (collapsible: history + update form) ─────── */

function ProblemRow({
  ticket,
  issueLabel,
  statuses,
  canWrite,
  authorId,
  onChanged,
}: {
  ticket: Ticket;
  issueLabel: string;
  statuses: TicketStatus[];
  canWrite: boolean;
  authorId: number;
  onChanged: () => Promise<void> | void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState<TicketHistory[] | null>(null);

  // Update form
  const [statusId, setStatusId] = useState(ticket.Status);
  const [note, setNote] = useState("");
  const [addToSolution, setAddToSolution] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Escalate to support
  const [escalating, setEscalating] = useState(false);
  const [escalateMsg, setEscalateMsg] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const alreadyEscalated = useMemo(
    () => (history ?? []).some((h) => h.Notes?.startsWith("Escalated to Support")),
    [history],
  );

  const loadHistory = useCallback(() => {
    setHistory(null);
    getHistoryEvents(ticket.ID)
      .then((r) => setHistory(!r.bError && r.oData ? r.oData : []))
      .catch(() => setHistory([]));
  }, [ticket.ID]);

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (next && history === null) loadHistory();
      return next;
    });
  }, [history, loadHistory]);

  const handleSave = useCallback(async () => {
    if (!note.trim() && statusId === ticket.Status) {
      setMsg({ type: "error", message: "Enter a note or change the status." });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const r = await addHistoryEvent(
        { TicketID: ticket.ID, StatusID: statusId, Notes: note.trim(), AdminUserID: authorId },
        addToSolution,
      );
      if (!r.bError && r.oData) {
        setMsg({ type: "success", message: "Update saved." });
        setNote("");
        setAddToSolution(false);
        loadHistory();
        await onChanged();
      } else {
        setMsg({ type: "error", message: r.sMessage ?? "Update failed." });
      }
    } catch (e: unknown) {
      setMsg({ type: "error", message: String(e) });
    } finally {
      setSaving(false);
    }
  }, [note, statusId, addToSolution, ticket.ID, ticket.Status, authorId, loadHistory, onChanged]);

  const handleEscalate = useCallback(async () => {
    setEscalating(true);
    setEscalateMsg(null);
    try {
      const r = await escalateTicketToZendesk(ticket.ID);
      if (!r.bError && r.oData) {
        setEscalateMsg({ type: "success", message: "Escalated to Support." });
        loadHistory();
        await onChanged();
      } else {
        setEscalateMsg({ type: "error", message: r.sMessage ?? "Escalation failed." });
      }
    } catch (e: unknown) {
      setEscalateMsg({ type: "error", message: String(e) });
    } finally {
      setEscalating(false);
    }
  }, [ticket.ID, loadHistory, onChanged]);

  return (
    <div style={{ border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-2)", overflow: "hidden" }}>
      {/* Header row */}
      <button
        onClick={toggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          padding: "var(--space-2) var(--space-3)",
          background: "var(--color-bg-muted)",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ color: "var(--color-text-secondary)", fontSize: "var(--text-xs)", width: 12 }}>
          {expanded ? "▾" : "▸"}
        </span>
        {statusBadge(ticket.Status)}
        <span style={{ fontWeight: "var(--font-semibold)" }}>#{ticket.ID}</span>
        <span style={{ color: "var(--color-text)" }}>{issueLabel}</span>
        <span
          style={{
            flex: 1,
            color: "var(--color-text-muted)",
            fontSize: "var(--text-sm)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {ticket.Description}
        </span>
        <span style={{ color: "var(--color-text-secondary)", fontSize: "var(--text-xs)", whiteSpace: "nowrap" }}>
          {formatDate(ticket.LastUpdated)}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: "var(--space-3)" }}>
          {canWrite && (
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
              <button
                className={styles.secondaryBtn}
                onClick={handleEscalate}
                disabled={escalating || alreadyEscalated}
                title="Creates a ticket directly in Zendesk"
              >
                {escalating ? "Escalating…" : alreadyEscalated ? "Escalated to Support" : "Escalate to Support"}
              </button>
              {escalateMsg && (
                <span
                  className={`${styles.statusMsg} ${escalateMsg.type === "success" ? styles.statusSuccess : styles.statusError}`}
                  style={{ margin: 0 }}
                >
                  {escalateMsg.message}
                </span>
              )}
            </div>
          )}
          {ticket.Description && (
            <div className={styles.fieldRowStacked} style={{ marginBottom: "var(--space-3)" }}>
              <label>Description</label>
              <div style={{ whiteSpace: "pre-wrap", fontSize: "var(--text-sm)" }}>{ticket.Description}</div>
            </div>
          )}
          <SolutionCallout solution={ticket.Solution} />

          {/* History timeline */}
          <h4 className={styles.sectionTitle} style={{ fontSize: "var(--text-sm)" }}>History</h4>
          <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: "var(--space-3)", paddingRight: "var(--space-2)" }}>
            <TicketHistoryTimeline history={history} formatDate={formatDate} />
          </div>

          {/* Update form */}
          {canWrite && ticket.Status !== CLOSED_STATUS && (
            <>
              <h4 className={styles.sectionTitle} style={{ fontSize: "var(--text-sm)" }}>Add update</h4>
              <div className={styles.fieldRow}>
                <label>Status</label>
                <select className={styles.fieldSelect} value={statusId} onChange={(e) => setStatusId(Number(e.target.value))}>
                  {statuses.map((s) => (
                    <option key={s.ID} value={s.StatusID}>{s.Title}</option>
                  ))}
                </select>
              </div>
              <div className={styles.fieldRowStacked}>
                <label>Note</label>
                <textarea
                  className={styles.fieldTextarea}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="What was done…"
                />
              </div>
              <label className={styles.checkLabel}>
                <input type="checkbox" checked={addToSolution} onChange={(e) => setAddToSolution(e.target.checked)} />
                Add to solution
              </label>
              <div className={styles.inlineActions} style={{ marginTop: "var(--space-2)" }}>
                <button className={styles.primaryBtn} onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save update"}
                </button>
              </div>
              {msg && (
                <div
                  className={`${styles.statusMsg} ${msg.type === "success" ? styles.statusSuccess : styles.statusError}`}
                  style={{ marginTop: "var(--space-2)" }}
                >
                  {msg.message}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── New problem form ─────────────────────────────────────── */

function NewProblemForm({
  kioskId,
  issueTypes,
  issueCategories,
  statuses,
  onCancel,
  onCreated,
}: {
  kioskId: number;
  issueTypes: TicketIssueType[];
  issueCategories: TicketIssueCategory[];
  statuses: TicketStatus[];
  onCancel: () => void;
  onCreated: () => Promise<void> | void;
}) {
  const [issueTypeId, setIssueTypeId] = useState<number | "">("");
  const [statusId, setStatusId] = useState(1);
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const issueOptions = useMemo(
    () => buildIssueTypeOptions(issueTypes, issueCategories),
    [issueTypes, issueCategories],
  );

  const handleCreate = useCallback(async () => {
    if (issueTypeId === "") {
      setMsg({ type: "error", message: "Select an issue type." });
      return;
    }
    setCreating(true);
    setMsg(null);
    try {
      // Guard: one open ticket per kiosk (matches the Tickets page behaviour).
      const existing = await getOpenTicketForKiosk(kioskId);
      if (!existing.bError && existing.oData && existing.oData.ID > 0 && existing.oData.Status !== CLOSED_STATUS) {
        setMsg({ type: "error", message: `An open problem (#${existing.oData.ID}) already exists for this kiosk.` });
        setCreating(false);
        return;
      }
      const r = await createTicket(kioskId, Number(issueTypeId), description, -1, statusId, true);
      if (!r.bError && r.oData) {
        await onCreated();
      } else {
        setMsg({ type: "error", message: r.sMessage ?? "Create failed." });
      }
    } catch (e: unknown) {
      setMsg({ type: "error", message: String(e) });
    } finally {
      setCreating(false);
    }
  }, [issueTypeId, statusId, description, kioskId, onCreated]);

  return (
    <div
      style={{
        border: "1px solid var(--color-border-default)",
        borderRadius: "var(--radius-2)",
        padding: "var(--space-3)",
        marginBottom: "var(--space-4)",
        background: "var(--color-bg-muted)",
      }}
    >
      <h4 className={styles.sectionTitle} style={{ marginTop: 0 }}>New problem</h4>
      <div className={styles.fieldRow}>
        <label>Status</label>
        <select className={styles.fieldSelect} value={statusId} onChange={(e) => setStatusId(Number(e.target.value))}>
          {statuses.map((s) => (
            <option key={s.ID} value={s.StatusID}>{s.Title}</option>
          ))}
        </select>
      </div>
      <div className={styles.fieldRow}>
        <label>Issue type</label>
        <SearchableSelect
          options={issueOptions}
          value={issueTypeId === "" ? "" : String(issueTypeId)}
          onChange={(v) => setIssueTypeId(v ? Number(v) : "")}
          placeholder="Search issue types…"
          ariaLabel="Issue type"
        />
      </div>
      <div className={styles.fieldRowStacked}>
        <label>Description</label>
        <textarea className={styles.fieldTextarea} value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
      </div>
      <div className={styles.inlineActions} style={{ marginTop: "var(--space-2)" }}>
        <button className={styles.secondaryBtn} onClick={onCancel} disabled={creating}>Cancel</button>
        <button className={styles.primaryBtn} onClick={handleCreate} disabled={creating}>
          {creating ? "Creating…" : "Create problem"}
        </button>
      </div>
      {msg && (
        <div
          className={`${styles.statusMsg} ${msg.type === "success" ? styles.statusSuccess : styles.statusError}`}
          style={{ marginTop: "var(--space-2)" }}
        >
          {msg.message}
        </div>
      )}
    </div>
  );
}
